/**
 * Grille de saisie des objectifs de CA (mensuels + annuel) pour les commerciaux d'une entité.
 * Lignes = commerciaux · colonnes = 12 mois + objectif annuel saisi à part.
 * Le manager navigue par année et enregistre toutes ses modifications d'un coup.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight, Save, Target } from 'lucide-react';
import { extractIdRef, fmtEuroCommercialDash, ymKey } from '../utils/commercialDashboardCa';

const MOIS_COURT = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];

/** Convertit une saisie texte (« 45 000 », « 45000,5 ») en nombre, ou null si vide. */
function parseAmount(raw) {
    if (raw == null) return null;
    const s = String(raw).trim().replace(/\s/g, '').replace(',', '.');
    if (s === '') return null;
    const n = parseFloat(s);
    return Number.isFinite(n) && n >= 0 ? n : null;
}

const CaObjectifsGrid = ({ apiUrl, entityId, commerciaux = [], definiParId, onSaved }) => {
    const [year, setYear] = useState(() => new Date().getFullYear());
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [okMsg, setOkMsg] = useState('');

    // Données serveur : maps clé → { id, montant }
    // mensuel : `${commercialId}|${YYYY-MM}` ; annuel : `${commercialId}`
    const [serverMonthly, setServerMonthly] = useState({});
    const [serverAnnual, setServerAnnual] = useState({});

    // Saisie en cours : mêmes clés → string
    const [draftMonthly, setDraftMonthly] = useState({});
    const [draftAnnual, setDraftAnnual] = useState({});

    const fetchData = useCallback(async () => {
        if (!entityId) return;
        setLoading(true);
        setError('');
        setOkMsg('');
        try {
            const [mR, aR] = await Promise.allSettled([
                axios.get(`${apiUrl}/ca_objectif_mensuels`, {
                    withCredentials: true,
                    params: { pagination: false, entite: `/api/entities/${entityId}` },
                    headers: { Accept: 'application/ld+json' },
                }),
                axios.get(`${apiUrl}/ca_objectif_annuels`, {
                    withCredentials: true,
                    params: { pagination: false, entite: `/api/entities/${entityId}`, annee: year },
                    headers: { Accept: 'application/ld+json' },
                }),
            ]);
            const member = (r) => {
                if (r.status !== 'fulfilled') return [];
                const d = r.value.data;
                return d['hydra:member'] || d.member || [];
            };
            const monthlyMap = {};
            member(mR).forEach((row) => {
                const cid = extractIdRef(row.commercial);
                if (cid == null || typeof row.moisRef !== 'string') return;
                if (row.moisRef.slice(0, 4) !== String(year)) return;
                monthlyMap[`${cid}|${row.moisRef}`] = { id: row.id, montant: Number(row.montantObjectif) };
            });
            const annualMap = {};
            member(aR).forEach((row) => {
                const cid = extractIdRef(row.commercial);
                if (cid == null || Number(row.annee) !== year) return;
                annualMap[`${cid}`] = { id: row.id, montant: Number(row.montantObjectif) };
            });
            setServerMonthly(monthlyMap);
            setServerAnnual(annualMap);
            const dM = {};
            Object.entries(monthlyMap).forEach(([k, v]) => { dM[k] = String(v.montant); });
            const dA = {};
            Object.entries(annualMap).forEach(([k, v]) => { dA[k] = String(v.montant); });
            setDraftMonthly(dM);
            setDraftAnnual(dA);
        } catch (e) {
            setError(e?.message || 'Erreur de chargement des objectifs.');
        } finally {
            setLoading(false);
        }
    }, [apiUrl, entityId, year]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const setCellMonthly = (cid, monthIndex, val) => {
        const k = `${cid}|${ymKey(year, monthIndex)}`;
        setDraftMonthly((prev) => ({ ...prev, [k]: val }));
        setOkMsg('');
    };
    const setCellAnnual = (cid, val) => {
        setDraftAnnual((prev) => ({ ...prev, [`${cid}`]: val }));
        setOkMsg('');
    };

    const totalsByCommercial = useMemo(() => {
        const out = {};
        commerciaux.forEach((c) => {
            let sum = 0;
            for (let m = 0; m < 12; m += 1) {
                const v = parseAmount(draftMonthly[`${c.id}|${ymKey(year, m)}`]);
                if (v != null) sum += v;
            }
            out[c.id] = sum;
        });
        return out;
    }, [commerciaux, draftMonthly, year]);

    const dirtyCount = useMemo(() => {
        let n = 0;
        commerciaux.forEach((c) => {
            for (let m = 0; m < 12; m += 1) {
                const k = `${c.id}|${ymKey(year, m)}`;
                const draft = parseAmount(draftMonthly[k]);
                const srv = serverMonthly[k]?.montant ?? null;
                if ((draft ?? null) !== (srv ?? null)) n += 1;
            }
            const ak = `${c.id}`;
            const draftA = parseAmount(draftAnnual[ak]);
            const srvA = serverAnnual[ak]?.montant ?? null;
            if ((draftA ?? null) !== (srvA ?? null)) n += 1;
        });
        return n;
    }, [commerciaux, draftMonthly, draftAnnual, serverMonthly, serverAnnual, year]);

    const handleSave = async () => {
        if (saving || dirtyCount === 0) return;
        setSaving(true);
        setError('');
        setOkMsg('');
        const ops = [];
        const definiPar = definiParId != null ? `/api/users/${definiParId}` : undefined;

        const upsert = (kind, key, cid, draftVal, srv, extra) => {
            const val = parseAmount(draftVal);
            const srvMontant = srv?.montant ?? null;
            if ((val ?? null) === (srvMontant ?? null)) return;
            const base = kind === 'mensuel' ? `${apiUrl}/ca_objectif_mensuels` : `${apiUrl}/ca_objectif_annuels`;
            if (val == null) {
                if (srv?.id != null) {
                    ops.push(axios.delete(`${base}/${srv.id}`, { withCredentials: true, headers: { Accept: 'application/ld+json' } }));
                }
                return;
            }
            if (srv?.id != null) {
                ops.push(axios.patch(`${base}/${srv.id}`, { montantObjectif: String(val) }, {
                    withCredentials: true,
                    headers: { Accept: 'application/ld+json', 'Content-Type': 'application/merge-patch+json' },
                }));
            } else {
                ops.push(axios.post(base, {
                    commercial: `/api/users/${cid}`,
                    entite: `/api/entities/${entityId}`,
                    montantObjectif: String(val),
                    ...(definiPar ? { definiPar } : {}),
                    ...extra,
                }, { withCredentials: true, headers: { Accept: 'application/ld+json', 'Content-Type': 'application/ld+json' } }));
            }
        };

        commerciaux.forEach((c) => {
            for (let m = 0; m < 12; m += 1) {
                const moisRef = ymKey(year, m);
                const k = `${c.id}|${moisRef}`;
                upsert('mensuel', k, c.id, draftMonthly[k], serverMonthly[k], { moisRef });
            }
            const ak = `${c.id}`;
            upsert('annuel', ak, c.id, draftAnnual[ak], serverAnnual[ak], { annee: year });
        });

        try {
            await Promise.all(ops);
            setOkMsg(`Objectifs enregistrés (${ops.length} modification${ops.length > 1 ? 's' : ''}).`);
            await fetchData();
            onSaved?.();
        } catch (e) {
            const msg = e?.response?.data?.['hydra:description'] || e?.response?.data?.detail || e?.message;
            setError(typeof msg === 'string' ? msg : 'Erreur lors de l’enregistrement.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="ed-card ed-objgrid">
            <div className="ed-card-hd ed-objgrid-hd">
                <span className="ed-card-title">
                    <Target size={13} strokeWidth={2.5} style={{ verticalAlign: 'middle', marginRight: 5 }} />
                    Objectifs de CA — saisie
                </span>
                <div className="ed-objgrid-year">
                    <button type="button" className="ed-objgrid-ybtn" onClick={() => setYear((y) => y - 1)} aria-label="Année précédente">
                        <ChevronLeft size={16} strokeWidth={2.5} />
                    </button>
                    <span className="ed-objgrid-yval">{year}</span>
                    <button type="button" className="ed-objgrid-ybtn" onClick={() => setYear((y) => y + 1)} aria-label="Année suivante">
                        <ChevronRight size={16} strokeWidth={2.5} />
                    </button>
                </div>
            </div>

            <p className="ed-objgrid-hint">
                Montants en € HT. La colonne <strong>Annuel</strong> est l’objectif annuel saisi à part&nbsp;; <strong>Σ mois</strong> est la somme des 12 mensuels.
                Laissez une case vide pour ne pas fixer d’objectif (ou pour le supprimer).
            </p>

            {loading ? (
                <p className="ed-objgrid-empty">Chargement…</p>
            ) : commerciaux.length === 0 ? (
                <p className="ed-objgrid-empty">Aucun commercial dans cette entité.</p>
            ) : (
                <div className="ed-objgrid-scroll">
                    <table className="ed-objgrid-table">
                        <thead>
                            <tr>
                                <th className="ed-objgrid-th-name">Commercial</th>
                                {MOIS_COURT.map((m) => <th key={m} className="ed-objgrid-th-mois">{m}</th>)}
                                <th className="ed-objgrid-th-annuel">Annuel</th>
                                <th className="ed-objgrid-th-sum">Σ mois</th>
                            </tr>
                        </thead>
                        <tbody>
                            {commerciaux.map((c) => (
                                <tr key={c.id}>
                                    <td className="ed-objgrid-td-name">{c.label}</td>
                                    {MOIS_COURT.map((m, idx) => {
                                        const k = `${c.id}|${ymKey(year, idx)}`;
                                        return (
                                            <td key={m} className="ed-objgrid-td-cell">
                                                <input
                                                    type="text"
                                                    inputMode="numeric"
                                                    className="ed-objgrid-input"
                                                    value={draftMonthly[k] ?? ''}
                                                    onChange={(e) => setCellMonthly(c.id, idx, e.target.value)}
                                                    placeholder="—"
                                                    autoComplete="off"
                                                    spellCheck={false}
                                                />
                                            </td>
                                        );
                                    })}
                                    <td className="ed-objgrid-td-cell ed-objgrid-td-annuel">
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            className="ed-objgrid-input ed-objgrid-input--annuel"
                                            value={draftAnnual[`${c.id}`] ?? ''}
                                            onChange={(e) => setCellAnnual(c.id, e.target.value)}
                                            placeholder="—"
                                            autoComplete="off"
                                            spellCheck={false}
                                        />
                                    </td>
                                    <td className="ed-objgrid-td-sum">{fmtEuroCommercialDash(totalsByCommercial[c.id] || 0)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="ed-objgrid-actions">
                {error && <span className="ed-objgrid-error">{error}</span>}
                {okMsg && !error && <span className="ed-objgrid-ok">{okMsg}</span>}
                <span className="ed-objgrid-dirty">{dirtyCount > 0 ? `${dirtyCount} modif. en attente` : 'Aucune modification'}</span>
                <button
                    type="button"
                    className="ed-objgrid-save"
                    onClick={handleSave}
                    disabled={saving || dirtyCount === 0}
                >
                    <Save size={14} strokeWidth={2.5} />
                    {saving ? 'Enregistrement…' : 'Enregistrer'}
                </button>
            </div>
        </div>
    );
};

export default CaObjectifsGrid;
